import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Info, Map as MapIcon, List, AlertTriangle } from "lucide-react";
import { FilterPanel } from "../components/search/FilterPanel";
import { ResultCard } from "../components/search/ResultCard";
import { ResultsMap } from "../components/search/ResultsMap";
import { Loading } from "../components/Layout";
import { PetPromo } from "./VetCuraPet";
import { api, errMsg } from "../lib/api";
import { SPECIES } from "../lib/labels";

const KEYS = ["species", "category", "service", "variant", "location", "radius", "region", "clinic", "price_min", "price_max", "price_type", "verified_only", "fixed_only", "has_booking", "has_prices", "sort", "page"];
const DEFAULTS = { species: "dog", radius: "25", sort: "price_asc", page: "1" };

const Pager = ({ page, pages, go }) => pages > 1 && (
  <nav className="flex items-center justify-center gap-2 mt-6" aria-label="Sideskift" data-testid="compare-pagination">
    <button className="btn-outline btn-sm" disabled={page <= 1} onClick={() => go(page - 1)} data-testid="pagination-prev">Forrige</button>
    <span className="text-sm text-[#52706D]" data-testid="pagination-status">Side {page} af {pages}</span>
    <button className="btn-outline btn-sm" disabled={page >= pages} onClick={() => go(page + 1)} data-testid="pagination-next">Næste</button>
  </nav>
);

const NearerWithoutPrice = ({ data, params }) => {
  const first = data.results[0]?.distance_km;
  const near = (data.clinics_without_price || []).filter((c) => c.distance_km != null && (first == null || c.distance_km < first));
  if (!data.service || !data.place || params.sort !== "distance" || data.page > 1 || !near.length) return null;
  return (
    <div className="card-vp p-4 mb-4" data-testid="nearer-without-price">
      <p className="text-sm font-semibold text-[#153C3A]">Tættere på dig – men uden offentliggjort pris for {data.service.name_da.toLowerCase()}</p>
      <ul className="mt-2 space-y-1 text-sm">{near.map((c) => <li key={c.clinic_slug}><Link to={`/dyreklinikker/${c.clinic_slug}`} className="text-[#087F72] hover:underline" data-testid={`nearer-clinic-${c.clinic_slug}`}>{c.clinic_name}</Link> <span className="text-xs text-[#52706D]">{c.city} · {c.distance_km.toLocaleString("da-DK")} km</span></li>)}</ul>
    </div>
  );
};

const Summary = ({ d }) => (
  <p className="text-sm text-[#52706D]" data-testid="compare-result-count">
    <strong className="text-[#153C3A]">{d.total}</strong> {d.total === 1 ? "lokation" : "lokationer"}
    {d.service && <> for <strong className="text-[#153C3A]">{d.service.name_da}</strong></>} · {SPECIES[d.species]}
    {d.place && <> · nær <strong className="text-[#153C3A]">{d.place.label}</strong> ({d.radius_km ? `${d.radius_km} km` : "hele landet"})</>}
    {d.service && <> · {d.counts.comparable} med sammenlignelig fast pris</>}
  </p>
);

export default function Compare() {
  const [sp, setSp] = useSearchParams();
  const params = useMemo(() => Object.fromEntries(KEYS.map((k) => [k, sp.get(k) || (k === "sort" && sp.get("location") ? "distance" : DEFAULTS[k]) || ""])), [sp]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("list");
  const [showFilters, setShowFilters] = useState(false);
  const set = (patch) => {
    const next = { ...params, page: "1", ...patch };
    setSp(Object.fromEntries(Object.entries(next).filter(([k, v]) => v !== "" && (k === "sort" ? v !== (next.location ? "distance" : "price_asc") : v !== DEFAULTS[k]))));
  };
  const reset = () => setSp(params.service ? { service: params.service, species: params.species } : {});
  useEffect(() => {
    api.get("/public/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/public/services", { params: { species: params.species } }).then((r) => setServices(r.data)).catch(() => {});
  }, [params.species]);
  useEffect(() => {
    setLoading(true); setError("");
    const q = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== ""));
    api.get("/public/search/v2", { params: q }).then((r) => setData(r.data)).catch((e) => { setError(errMsg(e)); setData(null); }).finally(() => setLoading(false));
  }, [params]);
  useEffect(() => {
    document.title = data?.service ? `${data.service.name_da} til ${SPECIES[params.species].toLowerCase()} – sammenlign priser | VetPris` : "Sammenlign dyrlægepriser for hund og kat | VetPris";
  }, [data, params.species]);
  const pick = (id) => { setSelected(id); document.getElementById(`res-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); };
  const variants = data?.variants || [];
  const fparams = { ...params, variant: params.variant || data?.auto_variant || "" };
  return (
    <div className="container-vp py-10" data-testid="compare-page">
      <p className="eyebrow">Prissammenligning</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-[#153C3A] mt-2">Sammenlign dyrlægepriser</h1>
      <div className="mt-4 flex items-start gap-2 rounded-xl bg-purple-50 border border-purple-200 p-3 text-sm text-purple-900" data-testid="fictional-data-notice">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />Priserne er hentet fra klinikkernes egne prislister og kontrolleret af VetPris. Fra-priser og intervaller er ikke faste priser. Den endelige pris skal altid bekræftes hos klinikken.
      </div>
      <div className="grid lg:grid-cols-[290px_1fr] xl:grid-cols-[290px_1fr_400px] gap-6 mt-8">
        <div>
          <button className="btn-outline btn-sm w-full lg:hidden mb-3" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters} data-testid="toggle-filters">{showFilters ? "Skjul filtre" : "Vis filtre"}</button>
          <div className={showFilters ? "block" : "hidden lg:block"}><FilterPanel params={fparams} set={set} reset={reset} services={services} categories={categories} variants={variants} /></div>
        </div>
        <section id="compare-results" aria-live="polite" className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            {data ? <Summary d={data} /> : <span />}
            <div className="flex items-center gap-2">
              <label htmlFor="f-sort" className="sr-only">Sortering</label>
              <select id="f-sort" className="field-vp h-9 text-sm w-auto" value={params.sort} onChange={(e) => set({ sort: e.target.value })} data-testid="compare-sort-select">
                <option value="price_asc">Laveste faste pris</option>
                <option value="price_desc">Højeste faste pris</option>
                <option value="distance" disabled={!data?.place}>Nærmeste klinik</option>
                <option value="recent">Senest kontrolleret</option>
              </select>
              <button className="btn-outline btn-sm xl:hidden" onClick={() => setView(view === "map" ? "list" : "map")} data-testid="toggle-map-view">{view === "map" ? <><List className="h-4 w-4" />Liste</> : <><MapIcon className="h-4 w-4" />Kort</>}</button>
            </div>
          </div>
          {data?.geocode_failed && <p className="mb-4 text-sm rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2" data-testid="geocode-failed"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />Vi kunne ikke finde “{params.location}”. Viser resultater for hele landet uden afstand. Prøv et 4-cifret postnummer eller et bynavn.</p>}
          {data?.service && data.counts.other > 0 && <p className="mb-4 text-xs text-[#52706D]" data-testid="comparison-note">Fra-priser, intervaller, estimater, pakkepriser og forældede priser rangeres efter de sammenlignelige faste priser.</p>}
          {view === "map" && data && <div className="h-[60vh] mb-4 xl:hidden"><ResultsMap markers={data.markers} selectedId={selected} onSelect={pick} center={data.place} /></div>}
          {loading && <Loading />}
          {error && <p className="text-red-700" data-testid="compare-error">{error}</p>}
          {data && !loading && !(view === "map" && typeof window !== "undefined" && window.innerWidth < 1280) && (
            <>
              <NearerWithoutPrice data={data} params={params} />
              <div className="space-y-4">{data.results.map((r, i) => <ResultCard key={r.location.id} r={r} i={i} selected={selected === r.location.id} onHover={setSelected} showPrice={!!data.service} />)}</div>
              {data.total === 0 && (
                <div className="card-vp p-8 text-center text-[#52706D]" data-testid="compare-no-results">
                  <p>Ingen klinikker matcher søgningen.</p>
                  <div className="flex flex-wrap justify-center gap-2 mt-4">{data.suggestions.map((s) => <button key={s.type} className="btn-outline btn-sm" onClick={() => (s.type === "reset" ? reset() : set(s.params))} data-testid={`suggestion-${s.type}`}>{s.label}</button>)}</div>
                </div>
              )}
              {data.service && data.missing_price_locations > 0 && <p className="text-xs text-[#52706D] mt-4" data-testid="missing-price-note">{data.missing_price_locations} klinikker i området har ingen offentliggjort pris for behandlingen.</p>}
              {data.service && data.clinics_without_price?.length > 0 && (
                <div className="card-vp p-4 mt-3" data-testid="clinics-without-price">
                  <p className="text-sm font-semibold text-[#153C3A]">Klinikker uden verificeret pris for denne behandling</p>
                  <ul className="mt-2 grid sm:grid-cols-2 gap-1 text-sm">{data.clinics_without_price.map((c) => <li key={c.clinic_slug}><Link to={`/dyreklinikker/${c.clinic_slug}`} className="text-[#087F72] hover:underline">{c.clinic_name}</Link> <span className="text-xs text-[#52706D]">{c.city}{c.distance_km != null ? ` · ${c.distance_km.toLocaleString("da-DK")} km` : ""}</span></li>)}</ul>
                </div>
              )}
              <Pager page={data.page} pages={data.pages} go={(p) => set({ page: String(p) })} />
              <div className="mt-8"><PetPromo testid="compare-vetcurapet-promo" /></div>
            </>
          )}
        </section>
        <div className="hidden xl:block"><div className="sticky top-24 h-[calc(100vh-8rem)]">{data && <ResultsMap markers={data.markers} selectedId={selected} onSelect={pick} center={data.place} />}</div></div>
      </div>
    </div>
  );
}
